import { Component, Element, Prop, State, h } from '@stencil/core';
import { Offer, Category, Product, Discount, Currency, BuyXGetYDetail, PageType, OfferType, BankOffer, DiscountDetailTable } from './interfaces';


/**
 * `salla-offer` is a StencilJS component that displays offers, categories, products, banks, and discount information.
 * It uses the `salla-slider` component for carousel functionality.
 *
 * @slot category - The slot for customizing the category entry layout.
 * 
 * @csspart custom - Custom CSS part to target specific elements in the component.
 *
 * @example
 * <salla-offer product-id="123"></salla-offer>
 */
@Component({
  tag: 'salla-offer',
  styleUrl: 'salla-offer.scss',
})
export class SallaOffer {

  constructor() {
    // Language
    salla.lang.onLoaded(() => {
      this.offer_with_price_text = salla.lang.get('pages.offer.with_price');
      this.with_discount_text = salla.lang.get('pages.products.with_a_discount');
      this.product_discount_text = salla.lang.get('pages.products.discount');
      this.special_offer_text = salla.lang.get('pages.products.special_offer');
      this.multipleBankOfferTitleText = salla.lang.get('pages.offer.multiple_bank_offers_title');
      this.multipleBankOfferTitleDescription = salla.lang.get('pages.offer.multiple_bank_offers_message');
      this.buy_quantity_text(0);
    })

    salla.onReady(() => {
      this.currentPage = salla.config.get('page.slug')
      this.userCurrency = salla.config.get('currencies')[salla.config.get('user.currency_code')].symbol
    });

    this.categorySlot = this.host.querySelector('[slot="category"]')?.innerHTML || '<a href={url} class="s-offer-slide-cat-entry"><i class={icon}></i><h4>{name}</h4></a>';
  }

  /**
   * Custom Card Component for the Salla Products List.
   *
   * This component allows you to customize the appearance of individual product cards within a Salla Products List.
   *
   * @example
   * <salla-products-list product-card-component="my-custom-card-style1" ...
   * <salla-products-list product-card-component="my-custom-card-style2" ...
   */
  @Prop() productCardComponent: string = 'custom-salla-product-card';

  // Declare component state variables
  @State() offersList: Offer[] = [];
  @State() userCurrency: Currency;
  @State() isMultipleBank: boolean = false;
  @State() title?: string;
  @State() currentPage: string;
  @State() hasCustomComponent: boolean;

  @State() isBankOffer: boolean = false;
  @State() canRender: boolean = false;
  @State() showOffer: boolean = salla.config.get('store.settings.product.show_special_offers');

  // Default translated texts
  @State() offer_with_price_text: string = salla.lang.get('pages.offer.with_price', { price: '' });
  @State() with_discount_text: string = salla.lang.get('pages.products.with_a_discount')
  @State() product_discount_text: string = salla.lang.get('pages.products.discount')
  @State() special_offer_text: string = salla.lang.get('pages.products.special_offer')
  @State() multipleBankOfferTitleText: string = salla.lang.get('pages.offer.multiple_bank_offers_title')
  @State() multipleBankOfferTitleDescription: string = salla.lang.get('pages.offer.multiple_bank_offers_message')
  @State() buy_quantity_text: (quantity: number) => string = (quantity) => salla.lang.get('pages.offer.buy_quantity', { quantity })

  // Reference to the host element
  @Element() host: HTMLElement;

  private categorySlot: string;

  private getEndpointByPageName(): string {
    return {
      [PageType.Cart]: `offers/cart/${salla.storage.get('cart.id')}`,
      [PageType.ProductDetail]: `offers/product/${salla.config.get('page.id')}`,
    }[this.currentPage] || "offers";
  }

  componentWillLoad() {
    this.hasCustomComponent = !!customElements.get(this.productCardComponent);
    // let offers = this.getOffersFromStorage();
    // if (offers) {
    //   return offers.then(offersFromStorage => this.offersList = offersFromStorage);
    // }

    return (new Promise(resolve => salla.onReady(resolve)))
      .then(() => {
        this.showOffer = !salla.url.is_page('product.single') || salla.config.get('store.settings.product.show_special_offers')
        if (this.showOffer) {
          return;
        }
        throw new Error("Merchant disabled showing the offers on product page");
      }
      )
      .then(() => salla.api.request(this.getEndpointByPageName()))
      .then((res) => {
        if (!(this.offersList = res.data).length) {
          throw new Error('salla-offers:: There is no offers!');
        }
        //we support these offers only
        const offer = this.offersList.find(offer => [OfferType.Bank, OfferType.BuyXGetY, OfferType.DiscountsTable].includes(offer.type as any));
        if (!offer) {
          throw new Error('salla-offers:: Offer type not supported yet!');
        }
        //because there is no need for special handling for discounts table, just skip the other cases
        if (offer.type === OfferType.DiscountsTable) {
          return this.offersList = [offer];
        }

        //if it's banks offer, we need to include the other banks overs
        if ((this.isBankOffer = offer.type === OfferType.Bank)) {
          this.offersList = this.offersList.filter(offer => offer.type === OfferType.Bank);
          this.isMultipleBank = this.offersList.length > 1;
          return this.offersList;
        }
        //BuyXGetY offers
        const getY = (offer.details as BuyXGetYDetail).get;

        return getY.source === 'products'
          //todo:: avoid this request, and the handling for the products, just use `salla-products-slider` and don't forget to pass the customcard
          ? salla.product.api.fetch({ source: "selected", source_value: getY.source_value })
            .then((response: any) => {
              getY.products = response.data;
              (offer.details as any).get = getY;
              return this.offersList = [offer];
            })//set the products
          : salla.product.api.categories()//get all categories in one query, then extract only the selected one, instead of sending multi requests
            .then((res) => {
              getY.categories = this.findCategories(res.data, getY.source_value);
              (offer.details as any).get = getY;
              return this.offersList = [offer];
            });
      })
      .then((offers: Offer[]) => {
        salla.storage.set(this.getStorageKey(), { offers, stored_at: new Date().getTime() });
        this.canRender = true;
      })
      .catch((error) => {
        salla.logger.warn(error)
      });
  }

  componentDidLoad() {
    let nav = this.host.querySelector('.s-slider-block__title-nav');
    nav?.classList.add("s-offer-bank-payment-nav")
  }


  findCategories(categories: Category[], ids: any[]): Category[] {
    let found: Category[] = [];

    for (const category of categories) {
      if (ids.includes(category.id_ || category.id)) { //here we are using || because we are planning to drop `id_`
        found.push(category);
      }
      if (category.sub_categories?.length > 0) {
        found = found.concat(this.findCategories(category.sub_categories, ids));
      }
    }

    return found;
  }

  //todo::add to the key params, to make sure it will support multi offers in the same page if it's wanted
  getStorageKey(): string {
    try {
      const pageSlug = salla.config.get('page.slug').replace('.', '_');
      const locale = salla.lang.getLocale();
      const currencyCode = salla.config.currency().code;

      if (!pageSlug || !locale || !currencyCode) {
        throw new Error('Unable to get the storage key.');
      }

      return `s-offers-${pageSlug}-${salla.config.get('page.id')}-${locale}-${currencyCode}`;
    } catch (error) {
      return '';
    }
  }

  //@ts-ignore
  getOffersFromStorage(): null | Promise<Offer[]> {
    let storageOffers = salla.storage.get(this.getStorageKey());
    //if the offers not existed, or it has been stored before 10 minutes from now, ignore it;
    if (!storageOffers || storageOffers.stored_at < (new Date().getTime() - 10 * 60 * 1000)) {
      salla.storage.remove(this.getStorageKey());
      return null;
    }
    this.canRender = true;

    //return it as resolve to support .then
    return Promise.resolve(storageOffers.offers);
  }

  render() {
    if (!this.offersList.length || !this.canRender || !this.showOffer) return null;
    const offer: Offer = this.offersList[0];

    const blockTitle = this.isBankOffer ? (this.isMultipleBank ? this.multipleBankOfferTitleText : null) : offer.title;
    const blockSubTitle = this.isBankOffer ? (this.isMultipleBank ? this.multipleBankOfferTitleDescription : null) : offer.description;

    const titles = {
      'block-title': blockTitle,
      'block-subTitle': blockSubTitle,
      'show-controls': this.isMultipleBank
    };

    return (
      <div class="s-offer-wrapper">
        {<p class="s-offer-corner-badge">{this.special_offer_text}</p>}
        {/* discounts table is not a slider, it's a table🤔 */}
        {/* single bank offer is not a slider, it's a dom without slider🤔 */}
        <salla-slider type="carousel" id="offer-slider" {...titles}>
          <div slot='items'>
            {this.renderSectionForOfferType(offer.type)}
          </div>
        </salla-slider>
      </div>
    );
  }

  private renderSectionForOfferType(offerType: OfferType | string) {
    if (this.isBankOffer) {
      return this.renderBankSection();
    }
    if (offerType == OfferType.BuyXGetY) {
      return this.renderBuyXGetYSection();
    }

    return this.renderDiscountTableSection();
  }

  /**
   * Generates content for the categories section.
   *
   * @param offeredCategories - An array of Category objects.
   * @returns An array of HTML elements representing categories.
   */
  private getCategoriesSection(category: Category) {
    return <div class="s-offer-slide-one-sixth swiper-slide" innerHTML={this.categorySlot
      .replace(/\{url\}/g, category.url)
      .replace(/\{icon\}/g, category.icon || "sicon-store")
      .replace(/\{name\}/g, category.name)}></div>
  }

  private renderBuyXGetYSection() {
    const details = (this.offersList[0].details as BuyXGetYDetail)
    return [
      details.get.products?.map((product: Product) => (
        <div class="s-offer-slide-one-fourth">
          {this.hasCustomComponent
            ? <this.productCardComponent product={product}></this.productCardComponent>
            : <salla-product-card shadow-on-hover product={product}></salla-product-card>}
        </div>
      )),
      details.get.categories?.map((category: Category) => this.getCategoriesSection(category))
    ];
  }

  /**
   * Generates content for the bank section.
   *
   * @param offeredBank - An array of Bank objects.
   * @param name - The title of the current offer.
   * @param description - The description of the current offer.
   * @returns An array of HTML elements representing banks.
   * 
   * TODO: loop over the list and filter bank types and pass it to this function
   */
  private renderBankSection() {
    return this.offersList.map((bankOffer: Offer) => {
      return <div class={{ "s-offer-slide-one-sixth": this.isMultipleBank, "s-offer-bank-wrapper-sinlge-item": !this.isMultipleBank }}>
        <div class={{ "s-offer-bank-wrapper": true, "s-offer-slide-one-sixth": !this.isMultipleBank, "s-offer-bank-wrapper-multi-spacer": this.isMultipleBank }}>
          <div class="s-offer-bank-logo">
            <img src={(bankOffer.details as BankOffer).logo || salla.url.cdn('images/s-empty.png')} data-src={(bankOffer.details as BankOffer).logo} alt={bankOffer.title + " offer"} />
          </div>
          <ul class="s-offer-bank-payment-wrapper">
            {(bankOffer.details as BankOffer).payments.map((payment: string) => {
              return <li class="s-offer-bank-payment-single">
                <img
                  src={salla.url.cdn('images/payment/' + payment + '.png') || salla.url.cdn('images/s-empty.png')}
                  data-src={salla.url.cdn('images/payment/' + payment + '.png')}
                  alt="payment" />
              </li>
            })}
          </ul>
          {this.isMultipleBank ?
            <p class="s-offer-bank-payment-discount-percent">
              {`${this.product_discount_text} ${(bankOffer.details as BankOffer).discount_value}${(bankOffer.details as BankOffer).discount_type === "percentage" && '%'}`}
            </p>
            : ""}
        </div>
        {!this.isMultipleBank ?
          <div class="s-offer-bank-message s-offer-slide-one-fourth">
            <h2>{this.offersList[0].title}</h2>
            <p innerHTML={this.generateBankDescription(this.offersList[0].description, (bankOffer.details as BankOffer).discount_value)}></p>
          </div>
          : ""}
      </div>
    })
  }

  private generateBankDescription(desc: string, value): string {
    return desc.replace(new RegExp(`${value} %`), `<span class="s-offer-bank-message-amount">${value} %</span>`)
  }

  /**
   * Generates content for the discount table section.
   *
   * @param discountTable - An array of Discount objects.
   * @returns An array of HTML elements representing discounts_table.
   */
  private renderDiscountTableSection() {
    const offer: Offer = this.offersList[0];
    let show_discounted_price = (offer.details as DiscountDetailTable).show_price_after_discount;
    return (this.offersList[0].details as DiscountDetailTable).discounts?.map((discount: Discount) => <div class="s-offer-slide-one-fourth">
      <div class="s-offer-slide-offer-entry">
        <div class="s-offer-slide-offer-entry-price-quantity-container">
          <p class="s-offer-slide-offer-entry-quantity">{this.buy_quantity_text(discount.quantity)}</p>
          {show_discounted_price ?
            <div class="s-offer-slide-offer-entry-price">
              <span>{this.offer_with_price_text}</span>
              <span class="s-offer-slide-offer-entry-price-amount">{discount.discounted_amount}</span> <span>{this.userCurrency}</span>
            </div> : ""}
        </div>
        <p class="s-offer-slide-offer-entry-price-amount-percent">{this.with_discount_text}
          (<span>{discount.percentage}{!!discount.percentage && '%'}</span>)</p>
      </div>
    </div>
    )
  }

}
